import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  ExternalLink,
  Info,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  XCircle,
  Zap,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type LemlistLead = {
  id: string;
  contactId: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  companyName: string | null;
  companyDomain: string | null;
  jobTitle: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  state: string | null;
  industry: string | null;
  companyWebsite: string | null;
  companyLinkedinUrl: string | null;
  companyDescription: string | null;
  companySize: string | null;
  companyEmployeeCount: number | null;
  companyFoundedYear: number | null;
  companyAddressStreet: string | null;
  companyPostalCode: string | null;
  companyCity: string | null;
  companyState: string | null;
  companyCountry: string | null;
  companyRevenue: number | null;
  companyRevenueYear: number | null;
  hasOpportunity?: boolean;
};

const ALL_STATUSES = "__all__";

export function SettingsLemlistPage() {
  const status = trpc.lemlist.status.useQuery();
  const [tab, setTab] = useState<"campaigns" | "imported" | "logs">("campaigns");

  if (status.isLoading) {
    return (
      <div className="mx-auto w-full max-w-screen-2xl px-4 py-8 md:px-6">
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!status.data?.configured) {
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6">
        <header className="mb-6">
          <h2 className="text-lg font-semibold">lemlist</h2>
          <p className="text-sm text-muted-foreground">
            Pick a lemlist campaign and create its contacts as CLIENT + OPPORTUNITY.
          </p>
        </header>
        <div className="rounded-lg border border-dashed p-8 text-center">
          <Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p className="text-sm font-medium">lemlist API key is not configured</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add an API key under Settings → Integrations, then come back here.
          </p>
          <Button asChild size="sm" className="mt-4">
            <Link to="/settings/integrations">
              <ExternalLink className="mr-1 h-3.5 w-3.5" />
              Go to Integrations
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-screen-2xl px-4 py-8 md:px-6">
      <header className="mb-6">
        <h2 className="text-lg font-semibold">lemlist</h2>
        <p className="text-sm text-muted-foreground">
          Manage campaigns and review opportunities imported from lemlist.
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="imported">Imported Opportunities</TabsTrigger>
          <TabsTrigger value="logs">Sync logs</TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns" className="mt-4">
          <CampaignsTab />
        </TabsContent>
        <TabsContent value="imported" className="mt-4">
          <ImportedOpportunitiesTab />
        </TabsContent>
        <TabsContent value="logs" className="mt-4">
          <SyncLogsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CampaignsTab() {
  const utils = trpc.useUtils();
  const saved = trpc.lemlist.listSavedCampaigns.useQuery();
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importingId, setImportingId] = useState<string | null>(null);
  const [debugDomain, setDebugDomain] = useState<string | null>(null);
  const [editingCampaign, setEditingCampaign] = useState<string | null>(null);

  const leads = trpc.lemlist.listCampaignLeads.useQuery(
    { campaignId: campaignId ?? "" },
    {
      enabled: Boolean(campaignId),
      staleTime: 60_000,
    }
  );

  const importMutation = trpc.lemlist.importSelected.useMutation({
    onSuccess: (data) => {
      const parts = [`Created: ${data.created}.`];
      if (data.skippedOpportunities > 0) {
        parts.push(
          `Skipped (client with domain already exists): ${data.skippedOpportunities}.`
        );
      }
      if (data.skipped > 0) parts.push(`Errors: ${data.skipped}.`);
      toast.success(parts.join(" "));
      utils.clients.list.invalidate();
      utils.opportunities.list.invalidate();
      utils.lemlist.listImportedOpportunities.invalidate();
    },
    onError: (err) => toast.error(err.message || "Import failed"),
    onSettled: () => setImportingId(null),
  });

  const toggleAutoImport = trpc.lemlist.updateCampaignSettings.useMutation({
    onSuccess: () => {
      utils.lemlist.listSavedCampaigns.invalidate();
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  const runNow = trpc.lemlist.runNightlyNow.useMutation({
    onSuccess: (data) => {
      toast.success(
        `Nightly run done: created ${data.created}, skipped ${data.skipped}, errors ${data.errors}.`
      );
      utils.lemlist.listSavedCampaigns.invalidate();
      utils.lemlist.listImportedOpportunities.invalidate();
      utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error(err.message || "Nightly run failed"),
  });

  const data = (leads.data ?? []) as LemlistLead[];
  const allCampaigns = saved.data ?? [];

  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const l of data) {
      if (l.state) set.add(l.state);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.filter((l) => {
      if (statusFilter !== ALL_STATUSES) {
        if ((l.state ?? "") !== statusFilter) return false;
      }
      if (!q) return true;
      return (
        (l.companyName ?? "").toLowerCase().includes(q) ||
        (l.companyDomain ?? "").toLowerCase().includes(q) ||
        (l.industry ?? "").toLowerCase().includes(q) ||
        (l.fullName ?? "").toLowerCase().includes(q) ||
        (l.email ?? "").toLowerCase().includes(q)
      );
    });
  }, [data, query, statusFilter]);

  const toggleId = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (filtered.every((l) => selectedIds.has(l.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const l of filtered) next.delete(l.id);
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const l of filtered) next.add(l.id);
        return next;
      });
    }
  };

  function leadToImportItem(lead: LemlistLead) {
    return {
      companyName:
        lead.companyName?.trim() || lead.companyDomain || lead.fullName || "—",
      companyDomain: lead.companyDomain,
      industry: lead.industry,
      addressStreet: lead.companyAddressStreet,
      postalCode: lead.companyPostalCode,
      city: lead.companyCity,
      country: lead.companyCountry,
      website: lead.companyWebsite,
      linkedinUrl: lead.companyLinkedinUrl,
      headcount: lead.companyEmployeeCount,
      foundedYear: lead.companyFoundedYear,
      revenueEur: lead.companyRevenue,
      revenueYear: lead.companyRevenueYear,
      description: lead.companyDescription,
      leadId: lead.id,
      leadState: lead.state,
      contact:
        lead.fullName || lead.email || lead.phone
          ? {
              fullName: lead.fullName,
              email: lead.email,
              phone: lead.phone,
            }
          : undefined,
    };
  }

  const onCreateOne = (lead: LemlistLead) => {
    setImportingId(lead.id);
    importMutation.mutate({
      campaignId: campaignId ?? null,
      companies: [leadToImportItem(lead)],
    });
  };

  const onCreateBulk = () => {
    const picked = data.filter((l) => selectedIds.has(l.id));
    if (picked.length === 0) {
      toast.error("Select at least one contact.");
      return;
    }
    setImportingId("__bulk__");
    importMutation.mutate({
      campaignId: campaignId ?? null,
      companies: picked.map(leadToImportItem),
    });
    setSelectedIds(new Set());
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((l) => selectedIds.has(l.id));
  const isImporting = importMutation.isPending;
  const editingCampaignRow = allCampaigns.find((c) => c.id === editingCampaign);

  return (
    <>
      <section className="mb-6 rounded-lg border p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold">Saved campaigns</h3>
            <p className="text-xs text-muted-foreground">
              Toggle which campaigns are included in the nightly opportunity
              import. The job runs daily at 03:00 UTC and creates opportunities
              for leads matching the trigger states.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => saved.refetch()}
              disabled={saved.isFetching}
            >
              {saved.isFetching ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1 h-3.5 w-3.5" />
              )}
              Sync from lemlist
            </Button>
            <Button
              size="sm"
              onClick={() => runNow.mutate()}
              disabled={runNow.isPending}
              title="Trigger the nightly job immediately for all enabled campaigns"
            >
              {runNow.isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="mr-1 h-3.5 w-3.5" />
              )}
              Run nightly now
            </Button>
          </div>
        </div>

        {saved.isLoading ? (
          <p className="text-xs text-muted-foreground">Loading campaigns…</p>
        ) : allCampaigns.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No campaigns yet. Click <em>Sync from lemlist</em> to import them.
          </p>
        ) : (
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Campaign</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Trigger states</th>
                  <th className="px-3 py-2 font-medium">Last import</th>
                  <th className="px-3 py-2 font-medium">Auto-import</th>
                  <th className="px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {allCampaigns.map((c) => {
                  const lastAt = c.lastImportedAt
                    ? new Date(c.lastImportedAt).toLocaleString()
                    : "—";
                  return (
                    <tr key={c.id} className="border-t hover:bg-muted/30">
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.id}</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        {c.status ? (
                          <Badge variant="secondary" className="text-[10px]">
                            {c.status}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-wrap gap-1">
                          {(c.triggerStates ?? []).map((s) => (
                            <Badge
                              key={s}
                              variant="outline"
                              className="text-[10px]"
                            >
                              {s}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-xs">
                        <div>{lastAt}</div>
                        {c.lastImportedAt && (
                          <div className="text-muted-foreground">
                            Created: {c.lastImportCount ?? 0}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Switch
                          checked={c.autoImportEnabled}
                          onCheckedChange={(v) =>
                            toggleAutoImport.mutate({
                              campaignId: c.id,
                              autoImportEnabled: v,
                            })
                          }
                          aria-label="Enable nightly auto-import"
                        />
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingCampaign(c.id)}
                          title="Edit trigger states"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mb-6 rounded-lg border p-4">
        <label className="mb-2 block text-sm font-medium">
          Manually browse and import a campaign
        </label>
        {allCampaigns.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No campaigns available. Click <em>Sync from lemlist</em> above.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={campaignId ?? ""}
              onValueChange={(v) => {
                setCampaignId(v || null);
                setSelectedIds(new Set());
                setQuery("");
                setStatusFilter(ALL_STATUSES);
              }}
            >
              <SelectTrigger className="h-9 min-w-[320px] flex-1">
                <SelectValue placeholder="Select a campaign…" />
              </SelectTrigger>
              <SelectContent>
                {allCampaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {campaignId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => leads.refetch()}
                disabled={leads.isFetching}
              >
                {leads.isFetching ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                )}
                Refresh
              </Button>
            )}
          </div>
        )}
      </section>

      {!campaignId && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Building2 className="mx-auto mb-2 h-5 w-5" />
          Select a campaign above to see and manually import its contacts.
        </div>
      )}

      {campaignId && leads.isError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{leads.error?.message ?? "lemlist error"}</span>
        </div>
      )}

      {campaignId && leads.isFetching && data.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
          Fetching contacts from lemlist…
        </div>
      )}

      {campaignId && !leads.isFetching && data.length === 0 && !leads.isError && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Building2 className="mx-auto mb-2 h-5 w-5" />
          This campaign has no contacts.
        </div>
      )}

      {campaignId && data.length > 0 && (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <div className="relative max-w-md flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by company, name or email…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
                  {availableStatuses.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-xs text-muted-foreground">
                Found {filtered.length}
                {data.length !== filtered.length && ` (of ${data.length})`}
              </p>
              <Button
                size="sm"
                onClick={onCreateBulk}
                disabled={isImporting || selectedIds.size === 0}
              >
                {isImporting && importingId === "__bulk__" ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="mr-1 h-3.5 w-3.5" />
                )}
                Create selected ({selectedIds.size})
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAll}
                      className="h-4 w-4 cursor-pointer"
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">Company info</th>
                  <th className="px-3 py-2 font-medium">Address</th>
                  <th className="px-3 py-2 font-medium">Contact</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="w-44 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    checked={selectedIds.has(lead.id)}
                    onToggle={() => toggleId(lead.id)}
                    onCreate={() => onCreateOne(lead)}
                    onInspect={
                      lead.companyDomain
                        ? () => setDebugDomain(lead.companyDomain)
                        : undefined
                    }
                    isCreating={isImporting && importingId === lead.id}
                    disabled={isImporting && importingId !== lead.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <InspectCompanyDialog
        domain={debugDomain}
        onClose={() => setDebugDomain(null)}
      />

      <EditTriggerStatesDialog
        campaign={editingCampaignRow ?? null}
        onClose={() => setEditingCampaign(null)}
      />

      {importMutation.data && (
        <section className="mt-6 rounded-lg border p-4">
          <h3 className="mb-2 text-sm font-semibold">Import result</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            Opportunities created: {importMutation.data.created}.{" "}
            {importMutation.data.skippedOpportunities > 0 &&
              `Skipped (client with domain already exists): ${importMutation.data.skippedOpportunities}. `}
            {importMutation.data.skipped > 0 &&
              `Errors: ${importMutation.data.skipped}.`}
          </p>
          {importMutation.data.results.length > 0 && (
            <ul className="space-y-1 text-sm">
              {importMutation.data.results.map((r, idx) => (
                <li
                  key={r.opportunityId ?? `${r.clientId}-${idx}`}
                  className="flex items-center gap-2"
                >
                  <CheckCircle2
                    className={cn(
                      "h-3.5 w-3.5",
                      r.skippedOpportunity ? "text-amber-600" : "text-emerald-600"
                    )}
                  />
                  <span className="truncate">{r.companyName}</span>
                  {r.skippedOpportunity ? (
                    <>
                      <Badge variant="secondary" className="text-[10px]">
                        contact added
                      </Badge>
                      {r.clientId && (
                        <Link
                          to={`/clients/${r.clientId}`}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          open client →
                        </Link>
                      )}
                    </>
                  ) : (
                    <>
                      <Badge variant="secondary" className="text-[10px]">
                        {r.clientCreated ? "new client" : "existing client"}
                      </Badge>
                      {r.opportunityId && (
                        <Link
                          to={`/o/${r.opportunityId}`}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          open opportunity →
                        </Link>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {importMutation.data.errors.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-destructive">
              {importMutation.data.errors.map((e, i) => (
                <li key={i} className="flex items-start gap-2">
                  <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    <strong>{e.companyName}:</strong> {e.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </>
  );
}

function ImportedOpportunitiesTab() {
  const list = trpc.lemlist.listImportedOpportunities.useQuery({ limit: 200 });
  const rows = list.data ?? [];

  return (
    <section className="rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Opportunities imported from lemlist</h3>
          <p className="text-xs text-muted-foreground">
            Latest {rows.length} opportunities marked as <code>source=lemlist</code>.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => list.refetch()}
          disabled={list.isFetching}
        >
          {list.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {list.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Building2 className="mx-auto mb-2 h-5 w-5" />
          No opportunities have been imported from lemlist yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Opportunity</th>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 font-medium">Contact</th>
                <th className="px-3 py-2 font-medium">Campaign</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const contact = [r.contactFirstName, r.contactLastName]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <tr key={r.id} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2 align-top">
                      <Link
                        to={`/o/${r.id}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {r.name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {r.clientId ? (
                        <Link
                          to={`/clients/${r.clientId}`}
                          className="hover:underline"
                        >
                          {r.clientName ?? "—"}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                      {r.clientWebsite && (
                        <div className="text-xs text-muted-foreground">
                          {r.clientWebsite}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div>{contact || "—"}</div>
                      {r.contactEmail && (
                        <div className="text-xs text-muted-foreground">
                          {r.contactEmail}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {r.campaignName ?? r.lemlistCampaignId ?? (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Badge variant="secondary" className="text-[10px]">
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-muted-foreground">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

type SyncLogDetail = {
  campaignId: string;
  campaignName: string;
  triggerStates: string[];
  leadsFetched: number;
  stateCounts: { state: string; count: number }[];
  matched: number;
  created: number;
  skippedOpportunities: number;
  errors: number;
  errorMessages: string[];
  status: "ok" | "no-match" | "skipped" | "error";
  note: string | null;
};

function SyncLogsTab() {
  const logs = trpc.lemlist.listSyncLogs.useQuery({ limit: 25 });
  const rows = logs.data ?? [];

  return (
    <section className="rounded-lg border p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">lemlist sync logs</h3>
          <p className="text-xs text-muted-foreground">
            Every nightly and manual run is recorded here — including the lead
            states found in each campaign, so you can see why leads were or
            weren&apos;t imported. The nightly job runs daily at 03:00 UTC.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => logs.refetch()}
          disabled={logs.isFetching}
        >
          {logs.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="mr-1 h-3.5 w-3.5" />
          )}
          Refresh
        </Button>
      </div>

      {logs.isLoading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <Info className="mx-auto mb-2 h-5 w-5" />
          No sync runs recorded yet. Use <em>Run nightly now</em> on the
          Campaigns tab to trigger one.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((log) => (
            <SyncLogRow key={log.id} log={log} />
          ))}
        </div>
      )}
    </section>
  );
}

function SyncLogRow({
  log,
}: {
  log: {
    id: string;
    trigger: string;
    status: string;
    startedAt: string | Date;
    durationMs: number | null;
    campaignsProcessed: number;
    created: number;
    skipped: number;
    errors: number;
    message: string | null;
    details: SyncLogDetail[];
  };
}) {
  const [open, setOpen] = useState(false);
  const details = (log.details ?? []) as SyncLogDetail[];
  const statusColor =
    log.status === "error"
      ? "text-destructive"
      : log.status === "skipped"
        ? "text-amber-600"
        : "text-emerald-600";
  const StatusIcon =
    log.status === "error"
      ? XCircle
      : log.status === "skipped"
        ? AlertTriangle
        : CheckCircle2;

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted/30"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <StatusIcon className={cn("h-4 w-4 shrink-0", statusColor)} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">
              {new Date(log.startedAt).toLocaleString()}
            </span>
            <Badge variant="outline" className="text-[10px]">
              {log.trigger}
            </Badge>
            {log.durationMs != null && (
              <span className="text-xs text-muted-foreground">
                {(log.durationMs / 1000).toFixed(1)}s
              </span>
            )}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {log.message ??
              `created ${log.created}, skipped ${log.skipped}, errors ${log.errors}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-[10px]">
          <Badge variant="secondary">{log.created} created</Badge>
          {log.errors > 0 && (
            <Badge variant="destructive">{log.errors} errors</Badge>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t px-3 py-2">
          {details.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No per-campaign detail recorded for this run.
            </p>
          ) : (
            <div className="space-y-3">
              {details.map((d) => (
                <SyncLogDetailRow key={d.campaignId} detail={d} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SyncLogDetailRow({ detail: d }: { detail: SyncLogDetail }) {
  const statusBadge =
    d.status === "error"
      ? { variant: "destructive" as const, label: "error" }
      : d.status === "no-match"
        ? { variant: "outline" as const, label: "no match" }
        : d.status === "skipped"
          ? { variant: "outline" as const, label: "skipped" }
          : { variant: "secondary" as const, label: "ok" };

  return (
    <div className="rounded border bg-muted/20 p-2.5">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{d.campaignName}</span>
        <Badge variant={statusBadge.variant} className="text-[10px]">
          {statusBadge.label}
        </Badge>
        <span className="text-xs text-muted-foreground">
          fetched {d.leadsFetched} · matched {d.matched} · created {d.created}
          {d.skippedOpportunities > 0 && ` · skipped ${d.skippedOpportunities}`}
          {d.errors > 0 && ` · errors ${d.errors}`}
        </span>
      </div>

      <div className="mb-1 flex flex-wrap items-center gap-1 text-xs">
        <span className="text-muted-foreground">Trigger states:</span>
        {d.triggerStates.length > 0 ? (
          d.triggerStates.map((s) => (
            <Badge key={s} variant="outline" className="text-[10px]">
              {s}
            </Badge>
          ))
        ) : (
          <span className="text-amber-600">none configured</span>
        )}
      </div>

      {d.stateCounts.length > 0 && (
        <div className="mb-1 flex flex-wrap items-center gap-1 text-xs">
          <span className="text-muted-foreground">States in campaign:</span>
          {d.stateCounts.map((s) => {
            const isTrigger = d.triggerStates.some(
              (t) => t.toLowerCase() === s.state.toLowerCase()
            );
            return (
              <Badge
                key={s.state}
                variant={isTrigger ? "secondary" : "outline"}
                className={cn(
                  "text-[10px]",
                  isTrigger && "border-emerald-500"
                )}
              >
                {s.state}: {s.count}
              </Badge>
            );
          })}
        </div>
      )}

      {d.note && (
        <div className="mt-1 flex items-start gap-1.5 rounded bg-amber-50 p-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{d.note}</span>
        </div>
      )}

      {d.errorMessages.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-destructive">
          {d.errorMessages.map((m, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{m}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatRevenue(value: number): string {
  if (value >= 1_000_000_000) return `€${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `€${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `€${(value / 1_000).toFixed(0)}K`;
  return `€${value.toLocaleString("en-US")}`;
}

function LeadRow({
  lead,
  checked,
  onToggle,
  onCreate,
  onInspect,
  isCreating,
  disabled,
}: {
  lead: LemlistLead;
  checked: boolean;
  onToggle: () => void;
  onCreate: () => void;
  onInspect?: () => void;
  isCreating: boolean;
  disabled: boolean;
}) {
  const company = lead.companyName ?? lead.companyDomain ?? "—";
  const cityRegion = [lead.companyCity, lead.companyState]
    .filter(Boolean)
    .join(", ");
  const hasAddress =
    lead.companyAddressStreet ||
    lead.companyPostalCode ||
    lead.companyCity ||
    lead.companyState ||
    lead.companyCountry;
  const employees =
    lead.companyEmployeeCount != null
      ? `${lead.companyEmployeeCount.toLocaleString("en-US")} employees`
      : lead.companySize ?? null;
  return (
    <tr
      className={cn(
        "border-t hover:bg-muted/30",
        lead.hasOpportunity && "bg-green-50 hover:bg-green-100",
        checked && "bg-blue-50/60 hover:bg-blue-50"
      )}
    >
      <td className="px-3 py-2 align-top">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="h-4 w-4 cursor-pointer"
        />
      </td>
      <td className="px-3 py-2 align-top">
        <div className="min-w-0">
          <div className="truncate font-medium">{company}</div>
          {lead.companyDomain && (
            <a
              href={`https://${lead.companyDomain}`}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              {lead.companyDomain}
            </a>
          )}
          {lead.companyLinkedinUrl && (
            <a
              href={lead.companyLinkedinUrl}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-xs text-blue-600 hover:underline"
            >
              LinkedIn
            </a>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <div className="space-y-1 text-xs">
          {lead.industry && (
            <div>
              <span className="text-muted-foreground">Industry:</span>{" "}
              <span>{lead.industry}</span>
            </div>
          )}
          {employees && (
            <div>
              <span className="text-muted-foreground">Employees:</span>{" "}
              <span>{employees}</span>
            </div>
          )}
          {lead.companyRevenue != null && (
            <div>
              <span className="text-muted-foreground">Revenue:</span>{" "}
              <span>
                {formatRevenue(lead.companyRevenue)}
                {lead.companyRevenueYear ? ` (${lead.companyRevenueYear})` : ""}
              </span>
            </div>
          )}
          {lead.companyFoundedYear != null && (
            <div>
              <span className="text-muted-foreground">Founded:</span>{" "}
              <span>{lead.companyFoundedYear}</span>
            </div>
          )}
          {lead.companyDescription && (
            <div
              className="line-clamp-2 text-muted-foreground"
              title={lead.companyDescription}
            >
              {lead.companyDescription}
            </div>
          )}
          {!lead.industry &&
            !employees &&
            lead.companyRevenue == null &&
            lead.companyFoundedYear == null &&
            !lead.companyDescription && (
              <span className="text-muted-foreground">—</span>
            )}
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        {hasAddress ? (
          <div className="space-y-0.5 text-xs">
            {lead.companyAddressStreet && (
              <div>{lead.companyAddressStreet}</div>
            )}
            {(lead.companyPostalCode || cityRegion) && (
              <div>
                {[lead.companyPostalCode, cityRegion]
                  .filter(Boolean)
                  .join(" ")}
              </div>
            )}
            {lead.companyCountry && (
              <div className="text-muted-foreground">
                {lead.companyCountry}
              </div>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        <div>{lead.fullName ?? "—"}</div>
        {lead.jobTitle && (
          <div className="text-xs text-muted-foreground">{lead.jobTitle}</div>
        )}
        {lead.email && (
          <div className="truncate text-xs text-muted-foreground">
            {lead.email}
          </div>
        )}
      </td>
      <td className="px-3 py-2 align-top">
        {lead.state ? (
          <Badge variant="secondary" className="text-[10px]">
            {lead.state}
          </Badge>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right align-top">
        <div className="flex items-center justify-end gap-1">
          {onInspect && (
            <button
              type="button"
              onClick={onInspect}
              title="View the full lemlist response for this company"
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          )}
          {lead.hasOpportunity ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Opportunity exists
            </span>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={onCreate}
              disabled={disabled || isCreating}
            >
              {isCreating ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="mr-1 h-3.5 w-3.5" />
              )}
              Create opportunity
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function InspectCompanyDialog({
  domain,
  onClose,
}: {
  domain: string | null;
  onClose: () => void;
}) {
  const enabled = Boolean(domain);
  const query = trpc.lemlist.inspectCompany.useQuery(
    { domain: domain ?? "" },
    { enabled, staleTime: 30_000 }
  );

  return (
    <Dialog open={enabled} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>lemlist response: {domain}</DialogTitle>
          <DialogDescription>
            This is the full <code>/api/companies</code> response for this company.
            If fields (employees, size, founded…) are empty, lemlist hasn't
            enriched it with their tools yet.
          </DialogDescription>
        </DialogHeader>
        {query.isLoading && (
          <div className="py-4 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Loading…
          </div>
        )}
        {query.isError && (
          <p className="text-sm text-destructive">{query.error?.message}</p>
        )}
        {query.data && (
          <>
            {!query.data.found && (
              <p className="text-sm text-muted-foreground">
                lemlist did not find this company.
              </p>
            )}
            <pre className="max-h-[60vh] overflow-auto rounded bg-muted/40 p-3 text-xs">
              {JSON.stringify(query.data.raw, null, 2)}
            </pre>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditTriggerStatesDialog({
  campaign,
  onClose,
}: {
  campaign:
    | {
        id: string;
        name: string;
        triggerStates: string[];
      }
    | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [customInput, setCustomInput] = useState("");

  const statesQuery = trpc.lemlist.getCampaignStates.useQuery(
    { campaignId: campaign?.id ?? "" },
    { enabled: Boolean(campaign), staleTime: 60_000 }
  );

  const update = trpc.lemlist.updateCampaignSettings.useMutation({
    onSuccess: () => {
      utils.lemlist.listSavedCampaigns.invalidate();
      toast.success("Trigger states updated.");
      onClose();
    },
    onError: (err) => toast.error(err.message || "Failed to update"),
  });

  useEffect(() => {
    if (campaign) {
      setSelected(new Set(campaign.triggerStates));
      setCustomInput("");
    }
  }, [campaign?.id]);

  const observed = statesQuery.data ?? [];
  const observedNames = new Set(observed.map((s) => s.state));

  const customStates = Array.from(selected).filter((s) => !observedNames.has(s));

  const toggle = (state: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(state)) next.delete(state);
      else next.add(state);
      return next;
    });
  };

  const addCustom = () => {
    const items = customInput
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (items.length === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      for (const s of items) next.add(s);
      return next;
    });
    setCustomInput("");
  };

  const onSave = () => {
    if (!campaign) return;
    update.mutate({
      campaignId: campaign.id,
      triggerStates: Array.from(selected),
    });
  };

  return (
    <Dialog
      open={Boolean(campaign)}
      onOpenChange={(o) => {
        if (!o) {
          setSelected(new Set());
          setCustomInput("");
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Trigger states</DialogTitle>
          <DialogDescription>
            Pick the lemlist lead states that should create an opportunity in
            the nightly job for <strong>{campaign?.name}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">
                Available statuses in this campaign
              </span>
              {statesQuery.isFetching && (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              )}
            </div>
            {statesQuery.isLoading ? (
              <p className="text-xs text-muted-foreground">Loading statuses…</p>
            ) : observed.length === 0 && !statesQuery.isFetching ? (
              <p className="text-xs text-muted-foreground">
                No leads with statuses yet. Add custom states below.
              </p>
            ) : (
              <div className="max-h-64 space-y-1 overflow-auto rounded border p-2">
                {observed.map(({ state, count }) => (
                  <label
                    key={state}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      checked={selected.has(state)}
                      onChange={() => toggle(state)}
                    />
                    <span className="flex-1">{state}</span>
                    <Badge variant="secondary" className="text-[10px]">
                      {count}
                    </Badge>
                  </label>
                ))}
              </div>
            )}
          </div>

          {customStates.length > 0 && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">
                Custom states (not currently in this campaign)
              </span>
              <div className="mt-1 space-y-1 rounded border p-2">
                {customStates.map((state) => (
                  <label
                    key={state}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 cursor-pointer"
                      checked={selected.has(state)}
                      onChange={() => toggle(state)}
                    />
                    <span className="flex-1">{state}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <span className="text-xs font-medium text-muted-foreground">
              Add custom status
            </span>
            <div className="mt-1 flex gap-2">
              <Input
                value={customInput}
                onChange={(e) => setCustomInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addCustom();
                  }
                }}
                placeholder="emailsOpened, emailsClicked, …"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCustom}
                disabled={!customInput.trim()}
              >
                Add
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={update.isPending}>
            {update.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Save ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
